// employeesSaga.ts

import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { API } from "../../api";
import {
  addEmployee,
  addEmployeeFailure,
  addEmployeeSuccess,
  getEmployees,
  getEmployeesFailure,
  getEmployeesSuccess,
  getEmployeeStatus,
  getEmployeeStatusFailure,
  getEmployeeStatusSuccess,
  getFilteredEmployees,
  getFilteredEmployeesFailure,
  getFilteredEmployeesSuccess,
  inviteEmployees,
  inviteEmployeesFailure,
  inviteEmployeesSuccess,
  removeEmployees,
  removeEmployeesFailure,
  removeEmployeesSuccess,
  updateEmployees,
  updateEmployeesFailure,
  updateEmployeesSuccess,
} from "../reducers/employeesSlice";

import { errorHandler } from "./common";

function* getEmployeesSaga () {
  try {
    // @ts-ignore
    const token = yield select((state) => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(API.get, "/masters", {
      params: {
        limit: '500',
      },
      headers: {
        Authorization: `Bearer ${  token}`,
      },
    });

    yield put(getEmployeesSuccess(response.data.data));
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield getEmployeesSaga();
      },
      *callback () {
        yield put(getEmployeesFailure(error));
      },
    });
  }
}

function* getFilteredEmployeesSaga (action: any) {
  try {
    const searchParams = action.payload;
    // @ts-ignore
    const token = yield select((state) => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(API.get, "/masters", {
      params: {
        limit: "500",
        ...searchParams,
      },
      headers: {
        Authorization: `Bearer ${  token}`,
      },
    });

    yield put(getFilteredEmployeesSuccess(response.data.data));
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield getFilteredEmployeesSaga(action);
      },
      *callback () {
        yield put(getFilteredEmployeesFailure(error));
      },
    });
  }
}

function* addEmployeeSaga ({
  payload: { token, password, onSuccess, onFailure },
}: {
  payload: {
    token: string;
    password: string;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}) {
  try {
    // @ts-ignore
    yield call<any>(API.post, "/masters/", {
      token,
      password,
    });

    yield put(addEmployeeSuccess());
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield addEmployeeSaga({
          payload: { token, password, onSuccess, onFailure },
        });
      },
      *callback () {
        yield put(addEmployeeFailure(error));
        onFailure(error);
      },
    });
  }
}

interface workplacesProp {
  studioId: number;
  skills: [
    {
      service: number;
      rank: string;
    }
  ];
  schedule: {
    workingTimes: [
      {
        period: {
          from: string;
          to: string;
        };
        daysPeriods: {
          1: {
            from: {
              hours: number;
              minutes: number;
            };
            to: {
              hours: number;
              minutes: number;
            };
          };
        };
      }
    ];
    daysOff: [
      {
        period: {
          from: string;
          to: string;
        };
        reason: string;
      }
    ];
  };
}

interface inviteEmployeesSagaProp {
  payload: {
    firstName: string;
    lastName: string;
    photo: string;
    email: string;
    specialty: string;
    workplaces: any;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}

function* inviteEmployeesSaga ({
  payload: {
    firstName,
    lastName,
    photo,
    email,
    specialty,
    workplaces,
    onSuccess,
    onFailure,
  },
}: inviteEmployeesSagaProp) {
  try {
    // @ts-ignore
    const token = yield select((state) => state.auth.token);
    // @ts-ignore
    yield call<any>(
      API.post,
      `/masters/invite`,
      {
        firstName,
        lastName,
        photo,
        email,
        specialty,
        workplaces,
      },
      {
        headers: {
          Authorization: `Bearer ${  token}`,
        },
      }
    );
    yield put(inviteEmployeesSuccess());
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield inviteEmployeesSaga({
          payload: {
            firstName,
            lastName,
            photo,
            email,
            specialty,
            workplaces,
            onSuccess,
            onFailure,
          },
        });
      },
      *callback () {
        yield put(inviteEmployeesFailure(error));
        onFailure(error);
      },
    });
  }
}

interface updateEmployeesSagaProp {
  payload: {
    employeeId: string;
    firstName: string;
    lastName: string;
    photo: string;
    specialty: string;
    workplaces: workplacesProp;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}

function* updateEmployeesSaga ({
  payload: {
    employeeId,
    firstName,
    lastName,
    photo,
    specialty,
    workplaces,
    onSuccess,
    onFailure,
  },
}: updateEmployeesSagaProp) {
  try {
    // @ts-ignore
    const token = yield select((state) => state.auth.token);
    // @ts-ignore
    yield call<any>(
      API.patch,
      `masters/${employeeId}`,
      {
        firstName,
        lastName,
        photo,
        specialty,
        workplaces,
      },
      {
        headers: {
          Authorization: `Bearer ${  token}`,
        },
      }
    );

    yield put(updateEmployeesSuccess());
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield updateEmployeesSaga({
          payload: {
            employeeId,
            firstName,
            lastName,
            photo,
            specialty,
            workplaces,
            onSuccess,
            onFailure,
          },
        });
      },
      *callback () {
        yield put(updateEmployeesFailure(error));
        onFailure(error);
      },
    });
  }
}

interface removeEmployeesSagaProp {
  payload: {
    employeeId: string;
    employeeStudios: any;
    onSuccess: () => void;
    onFailure: (error: any) => void;
  };
}

function* removeEmployeesSaga ({
  payload: { employeeId, employeeStudios, onSuccess, onFailure },
}: removeEmployeesSagaProp) {
  try {
    // @ts-ignore
    const token = yield select((state) => state.auth.token);
    // @ts-ignore
    yield all (
      employeeStudios.map((id: number) =>
        call<any>(
          API.delete,
          `/masters/${employeeId}?studioId=${id}`,
          {
            headers: {
              Authorization: `Bearer ${  token}`,
            },
          }
        )
      )
    );

    yield put(removeEmployeesSuccess());
    onSuccess();
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield removeEmployeesSaga({
          payload: { employeeId, employeeStudios, onSuccess, onFailure },
        });
        onFailure(error);
      },
      *callback () {
        yield put(removeEmployeesFailure(error));
        onFailure(error);
      },
    });
  }
}

interface getEmployeeStatusSagaSagaProp {
  payload: {
    employeeId: string;
    onSuccess: (response: any) => void;
    onFailure: (error: any) => void;
  };
}

function* getEmployeeStatusSaga ({
  payload: {employeeId, onSuccess, onFailure,},
}: getEmployeeStatusSagaSagaProp
) {
  try {
    // @ts-ignore
    const token = yield select((state) => state.auth.token);
    // @ts-ignore
    const response = yield call<any>(
      API.post,
      `/masters/${employeeId}/has-active-orders`,
      {},
      {
        headers: {
          Authorization: `Bearer ${  token}`,
        },
        withCredentials: true,
      });

    yield put(getEmployeeStatusSuccess());
    onSuccess(response.data.data)
  } catch (error: any) {
    yield errorHandler({
      error,
      *recallCallback () {
        yield getEmployeeStatusSaga({payload: {employeeId, onSuccess, onFailure,}});
      },
      *callback () {
        yield put(getEmployeeStatusFailure(error));
        onFailure(error);
      },
    });
  }
}

// Watcher Saga
export function* employeesSaga () {
  // @ts-ignore
  yield takeLatest(getEmployees().type, getEmployeesSaga);
  // @ts-ignore
  yield takeLatest(getFilteredEmployees().type, getFilteredEmployeesSaga);
  // @ts-ignore
  yield takeLatest(addEmployee().type, addEmployeeSaga);
  // @ts-ignore
  yield takeLatest(inviteEmployees().type, inviteEmployeesSaga);
  // @ts-ignore
  yield takeLatest(updateEmployees().type, updateEmployeesSaga);
  // @ts-ignore
  yield takeLatest(removeEmployees().type, removeEmployeesSaga);
  // @ts-ignore
  yield takeLatest(getEmployeeStatus().type, getEmployeeStatusSaga);
}
